/**
 * Champions FAQ Section
 * Phase 7E: FAQ section about Champion selection process
 *
 * Provides transparency about:
 * - Selection process and timeline
 * - Application requirements
 * - Time commitments and expectations
 * - Benefits and responsibilities
 *
 * Modern compact grid with modal - saves ~70% vertical space
 */

import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { X, HelpCircle, Clock, Users, Award, Shield, Zap, MessageCircle } from 'lucide-react';

interface FAQItem {
  question: string;
  answer: string;
  icon: React.ReactNode;
  category: 'process' | 'timeline' | 'commitment' | 'benefits';
}

type ChampionsFAQSectionProps = {
  cityName?: string;
  contactEmail?: string;
  applicationUrl?: string;
};

export function ChampionsFAQSection({
  cityName = 'your community',
  contactEmail = '',
  applicationUrl = '/apply-champion',
}: ChampionsFAQSectionProps) {
  const [selectedFAQ, setSelectedFAQ] = useState<FAQItem | null>(null);

  const faqs: FAQItem[] = [
    {
      question: "What's the selection process?",
      answer:
        "After submitting your interest form, you'll complete a detailed application covering your community involvement, network reach, feedback strategy, communication skills, time commitment, and long-term governance interest. Our team reviews applications on a rolling basis and scores them across 6 criteria (0-100 points). Top applicants are invited to join as Champions.\n\nOnboarding format depends on cohort size:\n• Small cohorts: We start with 1:1 calls\n• Larger cohorts: Small groups of 3-5 people to keep it personal and efficient",
      icon: <Shield className="w-5 h-5" />,
      category: 'process',
    },
    {
      question: "Does applying guarantee I'll be selected?",
      answer: `No. Submitting an application does not guarantee selection as a Community Champion. We carefully evaluate all applications based on:\n\n• Community involvement and leadership experience\n• Network reach and social connections in ${cityName}\n• Communication skills and ability to explain the platform\n• Time commitment and availability\n• Feedback strategy and follow-through ability\n• Overall compatibility and attitude\n\nWe're looking for Champions who are genuinely excited to help build something meaningful, not just checking boxes on a form. Quality matters more than quantity. If demand is high (e.g., 50 applicants for 10 spots), we'll prioritize applicants with the strongest community connections and most realistic feedback plans. If demand is low (e.g., 5 applicants), we'll still evaluate each application carefully to ensure a good fit.`,
      icon: <HelpCircle className="w-5 h-5" />,
      category: 'process',
    },
    {
      question: 'When will I hear back?',
      answer:
        "You'll receive a decision via email within 5-7 business days after submitting your application. If accepted, you'll also receive dashboard login credentials and an invitation to the Champions kickoff meeting. If waitlisted, we'll reach out if spots open up or when we expand to a second cohort in 2-3 months. All applicants receive a personalized response regardless of outcome.",
      icon: <Clock className="w-5 h-5" />,
      category: 'timeline',
    },
    {
      question: 'What are the time commitments?',
      answer:
        "Light, flexible, and no pressure. Here's the typical flow:\n\n1. Kickoff intel gathering: A 1:1 or small group conversation. Could be 30 minutes or up to 2-3 hours depending on how much you want to share.\n\n2. We go to work: Our engineering and design team builds a demo based on your input. Timing depends on scope and availability.\n\n3. Follow-up demo: When ready, we schedule at mutual convenience to show you what we built.\n\n4. Optional testing and feedback: Quick checks and notes as you have time. No deadlines.\n\n5. Optional community outreach: Only if you're excited and want to help spread the word.\n\nMost Champions spend about 1-3 hours in a month on average. There's no minimum time requirement and we won't assign tasks unless you opt in. Tell us your availability and we'll work around your schedule.",
      icon: <Clock className="w-5 h-5" />,
      category: 'commitment',
    },
    {
      question: "What if I'm not selected?",
      answer: `We're starting with a small Champions group (5-10 people) to gather intel and build something the community actually wants. Selection is competitive and based on multiple factors including community involvement, reach potential, communication skills, and overall compatibility.\n\nIf you're not selected for the first cohort:\n• You may be placed on a waitlist if spots open up\n• You'll be considered for future cohorts if we expand (typically 2-3 months later)\n• You'll still have the opportunity to participate in the free trial if/when the platform launches\n\nImportant: Everything depends on real feedback and real demand. We're not making promises about timelines or public launches until we know what people actually need and whether ${cityName} wants this tool.`,
      icon: <Users className="w-5 h-5" />,
      category: 'process',
    },
    {
      question: 'What do Champions get in return?',
      answer: `If selected and the platform launches in ${cityName}, Champions receive:\n\n• Free lifetime premium subscription (valued at $12-24/year)\n• Early access to all new features before public release\n• Leadership role in the community with profile badge\n• First priority for future governance roles (Community Directors, moderators)\n• Direct communication channel with the ToolboxLogic team\n• Recognition as a founding member of the ${cityName} community platform\n\nImportant caveat: These benefits are contingent on platform launch. Launch requires both sufficient Champion participation AND demonstrated community interest during the testing period. If either threshold isn't met, the platform may not launch in ${cityName}, and these benefits would not apply. Champions help us determine whether ${cityName} genuinely wants this tool.`,
      icon: <Award className="w-5 h-5" />,
      category: 'benefits',
    },
    {
      question: 'Do I need technical skills?',
      answer:
        "No technical skills required! Champions need strong community connections, communication skills, and a willingness to learn and provide honest feedback. We're looking for community leaders, not developers. If you can use Facebook or email, you can be a Champion.",
      icon: <Zap className="w-5 h-5" />,
      category: 'process',
    },
    {
      question: 'How many Champions will be selected?',
      answer: `We're selecting 7-20 Champions for the first cohort in ${cityName}. This keeps the group manageable for direct collaboration while ensuring diverse perspectives. If demand is high, we'll expand to a second cohort 2-3 months after launch.`,
      icon: <Users className="w-5 h-5" />,
      category: 'process',
    },
    {
      question: 'Can I withdraw after being accepted?',
      answer:
        "Absolutely. There's no penalty for changing your mind. We understand life happens and circumstances change. Just let us know as soon as possible so we can offer the spot to someone on the waitlist.",
      icon: <MessageCircle className="w-5 h-5" />,
      category: 'process',
    },
    {
      question: "What if I can't keep up with the commitment?",
      answer:
        "We understand that life can be unpredictable. If you need to scale back your participation, just let us know. We'd rather have you contribute at a reduced capacity than lose you entirely. The time commitment is a guideline, not a strict requirement. What matters most is quality feedback and honest communication about what's working and what isn't. If circumstances change mid-program, we can adjust expectations together.",
      icon: <MessageCircle className="w-5 h-5" />,
      category: 'commitment',
    },
    {
      question: 'What does the Champions flow look like?',
      answer: `A flexible, step-by-step path with no rigid timelines:\n\n1. Kickoff intel gathering: 1:1 or small group of 3-5 people. Length varies from 30 minutes up to 2-3 hours depending on what you want to share.\n\n2. We go to work: Our engineering and design team builds a demo. Timing varies based on scope.\n\n3. Follow-up demo: When ready, we schedule at mutual convenience to show you the features.\n\n4. Optional hands-on testing: Quick feedback as convenient. No pressure.\n\n5. Optional community outreach: If there's strong buy-in and you want to help gauge interest in your network.\n\n6. Growth path for outstanding Champions: You may be invited to join the community governance team. For example, helping administer the ${cityName} community app. This is a community leadership role (not a city government function) and can carry real influence in shaping how your community connects and makes decisions.`,
      icon: <Users className="w-5 h-5" />,
      category: 'process',
    },
  ];

  return (
    <section id="champions-faq" className="py-12 sm:py-16 md:py-20 bg-background">
      <div className="container px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          {/* Section Header */}
          <div className="text-center mb-8 sm:mb-12">
            <div className="inline-flex items-center justify-center w-12 h-12 sm:w-16 sm:h-16 rounded-full bg-primary/10 mb-4">
              <HelpCircle className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
            </div>
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold mb-3 sm:mb-4">
              Champions Program FAQ
            </h2>
            <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              Quick answers to your most common questions. Click any card to learn more.
            </p>
          </div>

          {/* Compact FAQ Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {faqs.map((faq, index) => (
              <Card
                key={index}
                onClick={() => setSelectedFAQ(faq)}
                className="cursor-pointer hover:shadow-lg hover:scale-105 transition-all duration-300 hover:border-primary/50 group"
              >
                <CardContent className="p-4 sm:p-5">
                  <div className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-white transition-colors">
                      {faq.icon}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-sm sm:text-base font-semibold leading-tight group-hover:text-primary transition-colors">
                        {faq.question}
                      </h3>
                      <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                        Click to read more →
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Modal Overlay */}
          {selectedFAQ && (
            <div
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
              onClick={() => setSelectedFAQ(null)}
            >
              <div
                className="bg-background rounded-lg shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-y-auto animate-in zoom-in-95 duration-200"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modal Header */}
                <div className="sticky top-0 bg-background border-b p-4 sm:p-6 flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1">
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      {selectedFAQ.icon}
                    </div>
                    <h3 className="text-lg sm:text-xl font-bold leading-tight flex-1">
                      {selectedFAQ.question}
                    </h3>
                  </div>
                  <button
                    onClick={() => setSelectedFAQ(null)}
                    className="flex-shrink-0 w-8 h-8 rounded-full hover:bg-accent flex items-center justify-center transition-colors"
                    aria-label="Close"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {/* Modal Content */}
                <div className="p-4 sm:p-6">
                  <div className="text-sm sm:text-base text-muted-foreground leading-relaxed whitespace-pre-line">
                    {selectedFAQ.answer}
                  </div>
                </div>

                {/* Modal Footer */}
                <div className="border-t p-4 sm:p-6 bg-accent/5">
                  <p className="text-sm text-muted-foreground text-center">
                    {/* Still have questions? Email us at{' '}
                    <a
                      href={`mailto:${contactEmail}`}
                      className="text-primary hover:underline font-medium"
                    >
                      {contactEmail}
                    </a> */}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Bottom CTA */}
          <div className="mt-8 sm:mt-12 text-center space-y-6">
            <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 sm:p-8">
              <h3 className="text-xl sm:text-2xl font-bold mb-3">Ready to Become a Champion?</h3>
              <p className="text-sm sm:text-base text-muted-foreground mb-6 max-w-2xl mx-auto">
                Join our beta testing program and help shape the future of community engagement in{' '}
                {cityName}.
              </p>
              <a
                href={applicationUrl}
                className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white bg-primary hover:bg-primary/90 rounded-lg transition-colors shadow-lg hover:shadow-xl"
              >
                Apply to be a Champion
              </a>
            </div>

            <p className="text-sm sm:text-base text-muted-foreground">
              {/* Questions? We're here to help at{' '}
              <a
                href={`mailto:${contactEmail}`}
                className="text-primary hover:underline font-medium"
              >
                {contactEmail}
              </a> */}
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
